import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [role="combobox"], [tabindex]:not([tabindex="-1"])';

/** Open state for a trigger-plus-panel pair. Pointer presses inside the panel's React tree,
 * including portalled menus such as `Select` options, do not count as outside. */
export function useDismissablePopover() {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const pressedInside = useRef(false);

    const close = useCallback((returnFocus = true) => {
        setIsOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        pressedInside.current = false;
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

        const handlePointerDown = () => {
            if (!pressedInside.current) close(false);
            pressedInside.current = false;
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [isOpen, close]);

    return {
        isOpen,
        triggerRef,
        panelRef,
        toggle: () => (isOpen ? close() : setIsOpen(true)),
        close,
        containerProps: {
            onPointerDownCapture: () => {
                pressedInside.current = true;
            },
            onKeyDown: (event: KeyboardEvent) => {
                if (event.key === 'Escape' && isOpen && !event.defaultPrevented) {
                    event.preventDefault();
                    close();
                }
            },
        },
    };
}
